import { useForm } from "@mantine/form"
import { FormInput, FormQuerySelector, Modal } from "@vesatogo/grass-core"
import toast from "react-hot-toast"
import { OperationContext } from "urql"
import SharedEntityGroup from "~/constants/SharedEntityGroup"
import {
  useAddUpdateSharedEntityMutation,
  useAddUpdateStorageLocationsMutation,
  useAllSiUnitsQuery,
  useAllStorageKindsQuery,
} from "~/generated/graphql"

interface AddSubLocationModalProps {
  isOpen: boolean
  onClose: () => void
  storageLocationId: string
  parentData: {
    name: string
    capacity: number
    capacity_unit_id: string
  }
  refetch: (opts?: Partial<OperationContext> | undefined) => void
  reset?: () => void
}

export const AddSubLocationModal = ({
  isOpen,
  onClose,
  storageLocationId,
  parentData,
  refetch,
  reset,
}: AddSubLocationModalProps) => {
  const form = useForm({
    initialValues: {
      kind: null as any,
      capacity: "",
      unit: null as any,
      name: "",
    },
    validate: {
      kind: value => (!value ? "Required" : null),
      capacity: value => (!value ? "Required" : null),
      unit: value => (!value ? "Required" : null),
      name: value => (!value ? "Required" : null),
    },
  })

  const { getInputProps: getFormInputProps } = form

  // Using singular name as found in generated/graphql.ts
  const [{ fetching: submitting }, addSubLocation] =
    useAddUpdateStorageLocationsMutation()
  const [{ fetching: sharedEntityFetching }, upsertSharedEntity] =
    useAddUpdateSharedEntityMutation()

  function inputProps(name: string, group?: SharedEntityGroup) {
    const props = getFormInputProps(name)
    return {
      ...props,
      onChange: async (e: any) => {
        if (group) {
          if (Array.isArray(e)) {
            const newValues = await Promise.all(
              e.map(async item => {
                if (item?.codename && item?.name && !item?.shared_id) {
                  const { data } = await upsertSharedEntity({
                    reference_id: item.codename,
                    group_name: group,
                    name: item.name,
                  })
                  return {
                    ...item,
                    shared_id: data?.insert_setu_trade_shared_entity_one?.id,
                  }
                }
                return item
              })
            )
            form.setFieldValue(name, newValues)
          } else if (e?.codename && e?.name && !e?.shared_id) {
            const { data } = await upsertSharedEntity({
              reference_id: e.codename,
              group_name: group,
              name: e.name,
            })
            form.setFieldValue(name, {
              ...e,
              shared_id: data?.insert_setu_trade_shared_entity_one?.id,
            })
          } else {
            props.onChange(e)
          }
        } else {
          props.onChange(e)
        }
      },
    }
  }

  const handleSubmit = async (values: typeof form.values) => {
    const { error } = await addSubLocation({
      input: {
        id: storageLocationId,
        name: parentData.name,
        capacity: parentData.capacity,
        capacity_unit_id: parentData.capacity_unit_id,
        sub_locations: [
          {
            name: values.name,
            kind_id: values.kind?.id,
            capacity: Number(values.capacity),
            capacity_unit_id: values.unit?.shared_id,
          },
        ],
      },
    })

    if (error) {
      toast.error(error.message)
    } else {
      toast.success("Sub-location added successfully")
      onClose()
      reset?.()
      refetch({
        requestPolicy: "network-only",
      })
    }
  }

  return (
    <Modal
      title="Add Sub-Location"
      isOpen={isOpen}
      onClose={onClose}
      bodyClassName="max-w-md overflow-visible"
      primaryActionButtonProps={{
        text: "Okay",
        onClick: form.onSubmit(handleSubmit) as any,
        loading: submitting,
      }}
      secondaryActionButtonProps={{
        text: "Cancel",
        onClick: onClose,
        variant: "outline",
        disabled: submitting,
      }}
    >
      <div className="p-4 flex flex-col gap-6">
        <FormInput
          label="Sub-Location"
          required
          {...inputProps("name")}
          placeholder="Enter"
        />
        <FormQuerySelector
          label="Storage Type"
          required
          {...inputProps("kind")}
          dataHook={useAllStorageKindsQuery}
          placeholder="Select"
        />
        <FormInput
          label="Capacity"
          required
          placeholder="Enter"
          {...inputProps("capacity")}
          type="number"
        />
        <FormQuerySelector
          label="Unit"
          required
          {...inputProps("unit", SharedEntityGroup.KiteSiUnit)}
          dataHook={useAllSiUnitsQuery}
          variables={{ type: ["Weight", "SKU"] }}
          placeholder="Select"
        />
      </div>
    </Modal>
  )
}
